import { LabelButton } from "../../ui/LabelButton";
import { RectSize } from "../../utils/RectSize";
import { LoaderFileKeys } from "../constant/AppConstants";
import { AppSettings } from "../managers/AppSettings";
import { ScrollableContentScreenBase } from "./ScrollableContentScreenBase";
import { DealCompleteContentId, DealCompleteContentScroll } from "./dealcomplete/DealCompleteContentScroll";
import { LayoutDataManager } from "../managers/LayoutDataManager";
import { ScreenManager } from "../../managers/ScreenManager";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { AppAudioPlayer, SoundEffectKeys } from "../../audio/AppAudioPlayer";
import { ButtonEvents } from "../../ui/ButtonBase";
import { VariationId } from "./TableScreen";
import { ScreenOrientationType } from "../../utils/GetOrientation";
import { DealCompleteResultsContent } from "./dealcomplete/DealCompleteResultsContent";
import { GameCompleteViewLayout } from "./dealcomplete/GameCompleteView";
import { ApplyScalingBias } from "../../utils/ApplyScalingBias";


/**
 * CONTINUE - deal complete
 * LOBBY - deal complete | game complete
 * NEW_GAME - game complete
 */
export enum DealCompleteScreenExitType {
    CONTINUE,
    LOBBY,
    NEW_GAME,
}

export class DealCompleteScreen
    extends ScrollableContentScreenBase {
    public btnsBg: Phaser.GameObjects.Image;
    public btnContinue: LabelButton;
    public btnLobby: LabelButton;
    public btnNewGame: LabelButton;
    protected taskData: any;
    // loc update vars
    protected curVariationId: VariationId;
    protected curLanguageId: LanguageId;
    protected gameCompleteViewLayout: GameCompleteViewLayout;
    protected isDirty: boolean;
    // ui elements
    protected uiItemsSpacingUnscaled: number = 60;
    protected uiItemsPaddingUnscaled = { horizontal: 0, vertical: 40 };
    protected uiItemsSpacing: number = 0;
    protected uiItemsPadding = { horizontal: 0, vertical: 0 };


    public override Init(
        variationId: VariationId = undefined,
        languageId: LanguageId = undefined,
    ): void {
        // > ApplyMixins (Showable) + SetTweenProps
        super.Init();
        this.curVariationId = variationId;
        this.curLanguageId = languageId;

        // this.name = "DealCompleteScreen";
    }

    public override Build(): void {
        // build subclass objects required by base
        // custom ContentsScrollPane
        // NOTE: add in base
        this.contentsScrollPane = new DealCompleteContentScroll(this.scene);
        this.contentsScrollPane.Init(false, LayoutDataManager.GetSharedData().general.scrollableContent.size.innerScale); // referenceWidth?
        this.contentsScrollPane.Build();

        // build base
        // > bg
        // > (add) scrollPane
        // > btnPrev
        // > btnNext
        // > title
        super.Build();

        // btnsBg
        this.btnsBg = this.scene.make.image({
            key: LoaderFileKeys.ui,
            frame: "rect-2x2-black.png",
            alpha: 0.8,
        });
        this.btnsBg.setOrigin(0, 1);
        this.add(this.btnsBg);

        // btnLobby
        this.btnLobby = new LabelButton(this.scene);
        this.btnLobby.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-oval7-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-oval7-bg-over.png" },
        });
        this.btnLobby.InitLabel(
            {
                out: {
                    color: "#fff",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                },
            }, new Phaser.Geom.Point(0,-8)
        );
        this.btnLobby.label.setOrigin(0.5);
        this.btnLobby.PostInit(40);
        // this.btnLobby.view.scaleY = 0.8;
        this.add(this.btnLobby);

        // btnNewGame
        this.btnNewGame = new LabelButton(this.scene);
        this.btnNewGame.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-oval8-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-oval8-bg-over.png" },
        });
        this.btnNewGame.InitLabel(
            {
                out: {
                    color: "#fff",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                },
            }, new Phaser.Geom.Point(0,-8)
        );
        this.btnNewGame.label.setOrigin(0.5);
        this.btnNewGame.PostInit(40);
        // this.btnNewGame.view.scaleY = 0.8;
        this.add(this.btnNewGame);

        // btnContinue
        this.btnContinue = new LabelButton(this.scene);
        this.btnContinue.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "btn-oval8-bg-out.png" },
            over: { texture: LoaderFileKeys.ui, frame: "btn-oval8-bg-over.png" },
        });
        this.btnContinue.InitLabel(
            {
                out: {
                    color: "#fff",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    metrics: AppSettings.metrics.montserratBold60,
                },
            }, new Phaser.Geom.Point(0,-8)
        );
        this.btnContinue.label.setOrigin(0.5);
        this.btnContinue.PostInit(40);
        // this.btnContinue.setData("baseScale", 0.8);
        // this.btnContinue.view.scaleY = 0.8;
        this.add(this.btnContinue);

        // update text view once
        // NOTE: only reupdates via @see this.SetData, if variationId or langaugeId changes;
        this.UpdateFromLocalizationChange();
    }

    public SetTaskData(taskData: any): void {
        this.taskData = taskData;
    }

    protected override RegisterListeners(): void {
        // > btnPrev
        // > btnNext
        // > scrollPane
        // > ScreenManagerEvents.RESIZE
        super.RegisterListeners();

        this.btnContinue.on(ButtonEvents.CLICK, this.OnBtnContinueClick, this);
        this.btnLobby.on(ButtonEvents.CLICK, this.OnBtnLobbyClick, this);
        this.btnNewGame.on(ButtonEvents.CLICK, this.OnBtnNewGameClick, this);
    }

    protected override UnregisterListeners(): void {
        super.UnregisterListeners();

        this.btnContinue.off(ButtonEvents.CLICK, this.OnBtnContinueClick);
        this.btnLobby.off(ButtonEvents.CLICK, this.OnBtnLobbyClick, this);
        this.btnNewGame.off(ButtonEvents.CLICK, this.OnBtnNewGameClick, this);
    }

    protected override OnShowStart(animated: boolean): void {
        const resultsContent = this.contentsScrollPane.GetContent(DealCompleteContentId.RESULTS) as DealCompleteResultsContent;
        resultsContent.gameComplete.Show(false);

        // resize
        // udpate scroll default
        // screen transition
        super.OnShowStart(animated);
    }

    protected override OnHideComplete(): void {
        const resultsContent = this.contentsScrollPane.GetContent(DealCompleteContentId.RESULTS) as DealCompleteResultsContent;
        resultsContent.gameComplete.Hide(false);

        super.OnHideComplete();
    }

    public SetData(
        variationId?: VariationId,
        languageId?: LanguageId,
        gameCompleteViewLayout?: GameCompleteViewLayout
    ): void {
        let isDirty: boolean = this.isDirty ?? false;
        this.isDirty = false;
        // display screen for new variationId
        if (variationId != undefined && this.curVariationId != variationId) {
            this.curVariationId = variationId;
            isDirty = true;
        }
        // display screen for new languageId
        if (languageId != undefined && this.curLanguageId != languageId) {
            this.curLanguageId = languageId;
            isDirty = true;
        }
        if (this.gameCompleteViewLayout != gameCompleteViewLayout) {
            this.gameCompleteViewLayout = gameCompleteViewLayout;
            // this.isDirty = true;
        }
        // if language or variation changed
        // console.log("isDirty?", isDirty);
        if (isDirty) {
            // update scroll data
            this.UpdateFromLocalizationChange();
        }

        // > OnResizeManual
        this.UpdateLayout();
    }

    protected OnBtnContinueClick(): void {
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        this.Hide();
        this.taskData.onComplete(this.taskData.taskType, { exitType: DealCompleteScreenExitType.CONTINUE });
    }

    private OnBtnLobbyClick(): void {
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        this.taskData.onComplete(this.taskData.taskType, { exitType: DealCompleteScreenExitType.LOBBY });
    }

    private OnBtnNewGameClick(): void {
        AppAudioPlayer.PlaySfx(SoundEffectKeys.click5);
        this.taskData.onComplete(this.taskData.taskType, { exitType: DealCompleteScreenExitType.NEW_GAME });
    }

    /**
     * This runs if there was change in variationId or langaugeId, just before show()
     */
    public override UpdateFromLocalizationChange(): void {
        // console.log("UpdateViewFromLocalizationChange", LanguageId[this.curLanguageId]);

        // > title
        // > scrollPane
        super.UpdateFromLocalizationChange();

        // buttons (both layouts)
        this.btnContinue.SetText(AppLocalization.GetSharedData().dealComplete.continue.itemLabel);
        this.btnLobby.SetText(AppLocalization.GetSharedData().dealComplete.lobby.itemLabel);
        this.btnNewGame.SetText(AppLocalization.GetSharedData().dealComplete.newGame.itemLabel);
    }

    /**
     * Update UI visibility
     */
    private UpdateLayout(): void {
        switch (this.gameCompleteViewLayout) {
            // deal complete, BUT game not complete yet
            case GameCompleteViewLayout.NONE:
                this.btnLobby.visible = true;
                this.btnContinue.visible = true;
                this.btnNewGame.visible = false;
                break;
            // deal complete, game complete
            case GameCompleteViewLayout.WIN:
            case GameCompleteViewLayout.LOSS:
                this.btnLobby.visible = true;
                this.btnContinue.visible = false;
                this.btnNewGame.visible = true;
                break;
        }

        // refresh UI orirntation
        this.OnResizeManual();
    }

    /**
     * Called via @see ScrollableContentScreenBase#OnShowStart: OnResizeManual
     */
    protected override OnResize(windowRect: RectSize, force: boolean = false): void {
        // console.log("OnResize");

        super.OnResize(windowRect);

        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const btnWidth: number = (this.scrollPaneWidth - 2 * this.uiItemsPadding.horizontal - this.uiItemsSpacing) * 0.5;
        const btnScale: number = btnWidth / this.btnContinue.view.displayWidth;
        const btnOffsetHorizontal: number = (this.uiItemsSpacing + btnWidth) * 0.5;
        const btnHeight: number = btnScale * this.btnContinue.view.displayHeight;
        const btnBgHeight: number = btnHeight + 2 * this.uiItemsPadding.vertical - 20;
        const btnBgHeightHalf: number = btnBgHeight * 0.5;
        
        this.uiItemsSpacing = this.uiItemsSpacingUnscaled * btnScale;
        this.uiItemsPadding.horizontal = this.uiItemsPaddingUnscaled.horizontal * btnScale;
        this.uiItemsPadding.vertical = this.uiItemsPaddingUnscaled.vertical * btnScale;

        this.btnsBg.setDisplaySize(windowRect.width, btnBgHeight);
        this.btnsBg.setPosition(0, windowRect.height);

        this.btnLobby.setScale(btnScale);
        this.btnLobby.setPosition(screenCenterX - btnOffsetHorizontal, windowRect.height - btnBgHeightHalf);
        this.btnNewGame.setScale(btnScale);
        this.btnNewGame.setPosition(screenCenterX + btnOffsetHorizontal, windowRect.height - btnBgHeightHalf);
        this.btnContinue.setScale(btnScale);
        this.btnContinue.setPosition(screenCenterX + btnOffsetHorizontal, windowRect.height - btnBgHeightHalf);
    }
}